import { DEFAULT_UNIT_PREFERENCES } from './units.js';
/**
 * Everyday fleet words that differ between English-speaking markets.
 *
 * ⚠️ **This is NOT translation, and the distinction is the whole design.** It renames a
 * handful of ordinary nouns for readers who use a different ordinary noun for the same
 * physical thing — a UK fleet says *registration*, a US fleet says *license plate*, and both
 * mean the sequence painted on the vehicle. Nothing here changes what the platform does.
 *
 * ⚠️ **REGULATED vocabulary is deliberately absent, and adding any would be a defect.**
 * A tachograph is a specific device required by UK and EU law, and the drivers'-hours
 * subsystem implements those rules — AETR, assimilated, GB domestic. The nearest American
 * equivalent is an ELD under FMCSA Hours of Service, which is a different device, different
 * limits and different law. Relabelling "Tachograph" as "ELD" for a US reader would be
 * WORSE than leaving it in British English: it would claim compliance with a regulation
 * this product does not implement, on the one screen where being wrong is most expensive.
 *
 * How a regulated subsystem is handled instead: it is ENTITLED, not translated.
 * `TACHOGRAPH` is a feature key that defaults off and gates nine API routes and two web
 * routes, so a customer in a market it does not serve simply is not granted it and the
 * whole subsystem is absent from their product. That mechanism already exists and is the
 * right one — a market this product cannot serve needs a feature built, not a word changed.
 *
 * ⚠️ **Spelling variants are deliberately absent too.** `organisation` / `organization`
 * appears in 96 files and no American misreads it; carrying both would mean maintaining two
 * copies of every carefully-worded sentence, which drift. Only words where a reader would
 * genuinely stumble are here, and the list was MEASURED against what the screens actually
 * say rather than imagined — nine places, not fifty-seven.
 */
export const TERM_KEYS = [
    'registration',
    'registrationPlural',
    'licence',
    /*
     * ⚠️ `PETROL` is a DRIVETRAIN value, and this renames only its display wording. The
     * stored enum stays `PETROL` in every response to every tenant — the `statusLabel` rule:
     * display is display, and the API's vocabulary is not a screen's to change.
     */
    'petrol',
];
/**
 * The base vocabulary, and the fallback for any locale with no override.
 *
 * ⚠️ British English is the base because the product is written in it — 96 files say
 * "organisation". A base nobody has to restate is what keeps this list to the words that
 * genuinely differ rather than a full copy of the UI per market.
 */
const BASE = {
    registration: 'registration',
    registrationPlural: 'registrations',
    licence: 'licence',
    petrol: 'Petrol',
};
/**
 * What each locale calls these, where it differs from the base.
 *
 * ⚠️ PARTIAL on purpose. An entry repeating the base would be a second place the same word
 * is written, and the day somebody corrects one they will not think to correct the other.
 * en-IE, en-AU, en-NZ and en-IN all use the British terms and therefore appear nowhere.
 */
const OVERRIDES = {
    'en-US': {
        registration: 'license plate',
        registrationPlural: 'license plates',
        licence: 'license',
        petrol: 'Gasoline',
    },
};
/** The word this reader uses, lower-cased as it appears mid-sentence. */
export function term(key, locale = DEFAULT_UNIT_PREFERENCES.locale) {
    /*
     * ⚠️ Takes a plain string, not `DisplayLocale`. The caller is the web app reading the
     * module value the shell set, and an unrecognised one — an older stored value, a locale
     * removed from the list — must fall through to the base word rather than throw. A screen
     * rendering `undefined` where a noun belongs is a worse failure than a British spelling.
     */
    return OVERRIDES[locale]?.[key] ?? BASE[key];
}
/**
 * The same word as a LABEL or the start of a sentence.
 *
 * ⚠️ Capitalises the first letter only, never each word. "License plate" is right and
 * "License Plate" is not — this product's labels are sentence case throughout, and a
 * helper that title-cased would quietly introduce a second convention wherever it was used.
 */
export function termTitle(key, locale = DEFAULT_UNIT_PREFERENCES.locale) {
    const word = term(key, locale);
    return word.charAt(0).toUpperCase() + word.slice(1);
}
//# sourceMappingURL=terms.js.map